import { SearchInput } from 'components/SearchInput/SearchInput'
import { useEffect, useState } from 'react'
import { useProperties } from 'hooks/useProperties'

import { Property } from 'api/clients/PropertyClient'
import { ReactComponent as CheckIcon } from 'assets/icons/check16.svg'
import ExistingPropertyActionItem from 'common/PropertyActions/ExistingPropertyActionItem'
import { Button } from 'components/Button'
import { uniqBy } from 'lodash'
import Styles from './PropertyFinder.module.scss'
import cx from 'classnames'

const PropertyFinder = ({
  className,
  onPropertySelected,
  selectedProperty,
  properties,
}: {
  onPropertySelected: (property: Property) => void
  selectedProperty?: Property
  className?: string
  properties: Property[]
}) => {
  const { filterByName } = useProperties()
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [showWorkspaceProperties, setShowWorkspaceProperties] = useState(false)

  useEffect(() => {
    if (searchQuery.length > 0) setShowWorkspaceProperties(true)
  }, [searchQuery.length])

  const filteredProperties = filterByName(properties, searchQuery)
  const filteredAndReorderedProperties =
    selectedProperty && filteredProperties.includes(selectedProperty)
      ? [selectedProperty].concat(filteredProperties)
      : filteredProperties
  const allSearchedProperties = uniqBy(filteredAndReorderedProperties, 'id')
  const searchedProperties = allSearchedProperties.filter((p) => p.is_default || p.scope_project_id || !!p.default_type)
  const mainProperties =
    !!selectedProperty &&
    allSearchedProperties.includes(selectedProperty) &&
    searchedProperties[0]?.id !== selectedProperty.id
      ? [selectedProperty].concat(searchedProperties)
      : searchedProperties
  const workspaceProperties = allSearchedProperties.filter(
    (p) => !p.is_default && !p.scope_project_id && !p.default_type && p.id !== selectedProperty?.id,
  )

  const renderProperty = (property: Property) => {
    const isSelected = selectedProperty && selectedProperty?.id === property.id

    return (
      <Button
        key={`property-${property.id}`}
        variant="tertiary"
        size="md"
        onPress={() => onPropertySelected(property)}
        className={cx(Styles.propertyRow, { [Styles.selected]: isSelected })}
        data-testid={`property-option-${property.id}`}
      >
        <ExistingPropertyActionItem key={`${property.id}`} property={property} />
        {property.id && isSelected && <CheckIcon data-testid="check-icon" />}
      </Button>
    )
  }

  return (
    <div className={className}>
      <div className={Styles.searchInputWrap}>
        <SearchInput
          autoFocus={true}
          className={Styles.searchInput}
          placeholder="Find a property..."
          onChange={(searchQuery: string) => setSearchQuery(searchQuery)}
          debounceInterval={300}
          data-testid="property-finder-search-input"
        />
      </div>

      <div className={Styles.searchedProps}>
        {mainProperties?.map((property: Property) => renderProperty(property))}
        {!!workspaceProperties.length && (
          <>
            {showWorkspaceProperties ? (
              <>
                <div className={Styles.searchedProps}>
                  <div className={Styles.workspaceHeader}>Workspace Properties</div>
                  {workspaceProperties?.map((property: Property) => renderProperty(property))}
                </div>
              </>
            ) : (
              <div className={Styles.showWorkspaceButton} onClick={() => setShowWorkspaceProperties(true)}>
                Show workspace properties...
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export { PropertyFinder }
